import styled from "styled-components";
import type { FC, ReactNode, ComponentType, ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";
import { colorsPalettes } from "@similarweb/styles";

const { carbon } = colorsPalettes;

const Container = styled.div`
    height: inherit;
    position: relative;
`;

const Fade = styled.div<{ height?: string; baseColor: string }>`
    pointer-events: none;
    height: ${({ height }) => height};
    position: absolute;
    width: 100%;
`;

const TopFade = styled(Fade)`
    ${({ baseColor }) => `background: linear-gradient(${baseColor} 0%, rgba(255, 255, 255, 0))`};
`;
const BottomFade = styled(Fade)`
    ${({ baseColor }) => `background: linear-gradient(rgba(255, 255, 255, 0), ${baseColor} 100%)`};
    bottom: 0px;
`;

export const DefaultContentContainer = styled.div`
    height: 100%;
    width: 100%;
    overflow: hidden;
    /* leave empty space of 16px for the scroll bar */
    padding-right: 16px;
    box-sizing: border-box;
    &:hover {
        overflow-y: auto;
    }
`;
const SCROLL_OVERLAP_THRESHOLD = 0;

interface IFadeWrapperProps {
    children: ReactNode;
    fadeHeight?: string;
    topFadeHeight?: string;
    fadeBaseColor?: string;
    bottomFadeHeight?: string;
    TopFadeComponent?: ComponentType<ComponentProps<typeof TopFade>>;
    BottomFadeComponent?: ComponentType<ComponentProps<typeof BottomFade>>;
    ContentContainer?: ComponentType<ComponentProps<typeof DefaultContentContainer>>;
}
export const FadeWrapper: FC<IFadeWrapperProps> = (props) => {
    const {
        children,
        fadeHeight,
        fadeBaseColor = carbon[0],
        TopFadeComponent = TopFade,
        BottomFadeComponent = BottomFade,
        ContentContainer = DefaultContentContainer,
    } = props;
    const { topFadeHeight = fadeHeight, bottomFadeHeight = fadeHeight } = props;
    const [displayTopFade, setDisplayTopFade] = useState<boolean>(false);
    const [displayBottomFade, setDisplayBottomFade] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const onScroll = (event: any) => {
        const { target } = event;
        const { scrollTop, clientHeight, scrollHeight } = target;
        const scrollBottom = scrollHeight - scrollTop - clientHeight;
        const displayFadeTop = scrollTop > SCROLL_OVERLAP_THRESHOLD;
        const displayFadeBottom = scrollBottom > SCROLL_OVERLAP_THRESHOLD;
        setDisplayTopFade(displayFadeTop);
        setDisplayBottomFade(displayFadeBottom);
    };
    useEffect(() => {
        if (!containerRef?.current) {
            return;
        }
        const { clientWidth, clientHeight, scrollWidth, scrollHeight } = containerRef.current;
        const isOverflown = scrollHeight > clientHeight || scrollWidth > clientWidth;
        if (isOverflown) {
            setDisplayBottomFade(true);
        }
    }, [children]);

    return (
        <Container>
            {displayTopFade && (
                <TopFadeComponent
                    // @ts-ignore FIXME: strict migration - No overload matches — styled-components ComponentType prop spreading
                    height={topFadeHeight}
                    baseColor={fadeBaseColor}
                />
            )}
            <ContentContainer
                // @ts-ignore FIXME: strict migration - No overload matches — styled-components ComponentType ref/onScroll props
                ref={containerRef}
                onScroll={onScroll}
            >
                {children}
            </ContentContainer>
            {displayBottomFade && (
                <BottomFadeComponent
                    // @ts-ignore FIXME: strict migration - No overload matches — styled-components ComponentType prop spreading
                    height={bottomFadeHeight}
                    baseColor={fadeBaseColor}
                />
            )}
        </Container>
    );
};
